import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createServiceToken, fetchServiceTokens, revokeServiceToken } from './api';
import { ServiceTokenCreateRequest } from './types';

const SERVICE_TOKEN_KEY = 'service-tokens';

/**
 * Query keys for service tokens
 */
export const serviceTokensKeys = {
  all: [SERVICE_TOKEN_KEY] as const,
  lists: () => [...serviceTokensKeys.all, 'list'] as const,
  list: (userId: number, filters?: { isActive?: boolean }) =>
    [...serviceTokensKeys.lists(), userId, { ...filters }] as const,
};

/**
 * Hook to fetch service tokens for a user
 */
export const useServiceTokens = (userId: number, params?: { isActive?: boolean }) => {
  return useQuery({
    queryKey: serviceTokensKeys.list(userId, params),
    queryFn: () => fetchServiceTokens(userId, params),
    enabled: typeof userId === 'number' && !Number.isNaN(userId) && userId > 0,
  });
};

/**
 * Hook to create a service token
 */
export const useCreateServiceToken = (userId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ServiceTokenCreateRequest) => createServiceToken(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceTokensKeys.lists() });
    },
  });
};

/**
 * Hook to revoke a service token
 */
export const useRevokeServiceToken = (userId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tokenId: number) => revokeServiceToken(userId, tokenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceTokensKeys.lists() });
    },
  });
};
